import Store from './state/store';
import axios from 'axios';
import axiosCancel from 'axios-cancel';
import Auth from './auth';
import AngularAppModule from './app.module';
import * as ReportActions from './state/reports/reports.actions';
import * as FavouriteActions from './state/favourite/favourite.action';
import * as AppActions from './state/app/app.actions';
import * as UserActions from './state/user/user.actions';
import cssVars from 'css-vars-ponyfill';
import AppSettings from './app.settings';
import * as amplitude from '@amplitude/analytics-browser';

if (process.env.NODE_ENV === 'testing') {
  Auth.setUser({
    token_type: 'Bearer',
    access_token: 'faketoken'
  });
}

class App {
  constructor() {
    this.initialized = false;

    this.axios = axios.create();
    axiosCancel(this.axios);

    this.store = Store.create();
    this.amplitude = amplitude;

    // ponyfill settings
    cssVars({
      // Connect MutationObserver for css link update
      watch: true
    });
  }

  checkLocalStorageHack() {
    return localStorage.getItem('portal-bypass');
  }

  async init() {
    if (this.initialized) {
      return;
    }

    try {
      await Auth.init();

      if (process.env.NODE_ENV !== 'testing') {
        let userLoggedIn = await Auth.userIsLoggedIn();

        if (!userLoggedIn) {
          Auth.startAuthentication();
          return;
        }
      }
    } catch (err) {
      console.log('Access token already exists or initialization has failed.'); // eslint-disable-line no-console
    }

    const claims = Auth.getClaims();

    if (claims) {
      this.store.dispatch(UserActions.receiveUserClaims(claims));
      const brand = claims['tenant:brand'];
      this.loadBranding(brand);
    }

    this.configureAxios();

    this.store.dispatch(AppActions.initialize());

    this.configureAmplitude();

    AngularAppModule.init();

    this.initialized = true;
  }

  getStore() {
    return this.store;
  }

  configureAxios() {
    this.axios.defaults.headers.common['Content-Type'] = 'application/json';

    this.axios.interceptors.request.use(
      config => {
        config.headers.Authorization = Auth.getAuthorizationHeader();
        return config;
      },
      err => Promise.reject(err)
    );
  }

  configureAmplitude() {
    amplitude.init(AppSettings.AmplitudeAPIKEY, {
      defaultTracking: true
    });
  }

  redirectToLogin() {
    window.location.href = '/home';
  }

  getAxios() {
    return this.axios;
  }

  loadBranding(brand = 'onei3') {
    //add brand css link to the DOM file
    const head = document.head;
    //Favicon cache busting
    const randomVersion = Math.floor(Math.random() * Math.floor(10000)) + 1;
    // Fav icon
    const favicon = document.createElement('link');
    favicon.rel = 'icon';
    favicon.href = `${
      AppSettings.BrandingImageUrl
      }/c_scale,q_auto:best,w_32,c_fill${brand === 'onei3' ? ',b_rgb:003366' : ''}/v1/b/${brand}/mobile.png?v=${randomVersion}`;
    head.appendChild(favicon);

    // Add brand colouration
    const brandCSSLink = document.createElement('link');
    brandCSSLink.type = 'text/css';
    brandCSSLink.rel = 'stylesheet';
    brandCSSLink.href = `${AppSettings.BrandingFileUrl}/v1/b/${brand}/brand.css`;
    head.appendChild(brandCSSLink);

    // Add any special css that the protal needs for the brand
    const webPortalCSSLink = document.createElement('link');
    webPortalCSSLink.type = 'text/css';
    webPortalCSSLink.rel = 'stylesheet';
    webPortalCSSLink.href = `${AppSettings.BrandingFileUrl}/v1/b/${brand}/webportal.css`;
    head.appendChild(webPortalCSSLink);

    //set branding for mobile logo
    for (const size of AppSettings.MobileIconSizes) {
      const mobileLink = document.createElement('link');
      mobileLink.size = `${size}x${size}`;
      mobileLink.rel = 'apple-touch-icon';
      // We shoudl update this to use branding colour to update mobile icon transparencies if they exist
      const url = `${
        AppSettings.BrandingImageUrl
        }/c_scale,q_auto:best,w_${size},c_fill,b_rgb:003366/v1/b/${brand}/mobile.png`;
      mobileLink.href = url;
      head.appendChild(mobileLink);
    }

    //Set brand logo
    const brandLogoUrl = `${AppSettings.BrandingImageUrl}/v1/b/${brand}/logo.png`;

    //load brand logo to init loading
    const initialLoadingImg = document.getElementById('initial-loading-logo');
    initialLoadingImg.src = brandLogoUrl;

    //Set onei3 corner icon
    var onei3CornerIcon = `${AppSettings.BrandingImageUrl}/c_scale,q_auto:best,w_144/v1/b/onei3/mobile.png`;
    if (brand !== 'onei3') {
      onei3CornerIcon = `${AppSettings.BrandingImageUrl}/c_scale,q_auto:best,w_144/v1/b/onei3/mobile-white.png`;
    }

    this.store.dispatch(AppActions.appBranding(brandLogoUrl, onei3CornerIcon));
  }
}

export default new App();
